import { faInfoCircle } from '@awesome.me/kit-b4fd369ea4/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from '../FormContext';
import { HeaderTooltip } from '../headerTooltip/HeaderTooltip';
import { InfoTooltip } from '../headerTooltip/InfoTooltip';
import { useFormField } from './useFormField';

/**
 * Augments useFormField to provide additional standard logic for FormFields powered by a fieldDef
 *
 * This is generally tied to DynamicFormField and should only be used with FormField components that control
 * a single key in the formValues object
 */
export const useFormFieldDef = (fieldDef) => {
  const { formValues, assignValue } = useForm();

  // Field defs may carry a default value, this should be set on the form upon mounting
  useEffect(() => {
    // If there is no defaultValue, or the form already has a value for this field given from the parent, we back off
    if (!fieldDef.defaultValue || formValues[fieldDef.field] !== undefined) return;
    assignValue(fieldDef.field, fieldDef.defaultValue); 
  }, []);

  // Prepare validate function
  const validate = () => {

    const value = formValues[fieldDef.field];

    const validators = fieldDef.validators ?? [];
    for (const validator of validators) {
      const errorState = validator(value, formValues); // Validators come in the form of functions that can take value and formValues as arguments
      if (errorState) return errorState;
    }

    if (fieldDef.required) {
      const error = value == null || value === '' ? `${fieldDef.displayName} is required` : null;
      return error;
    }

    return null;
  };

  // Pass necessary information from fieldDef into useFormField
  const { onBlur, error, helperText } = useFormField(fieldDef.field, [fieldDef.field], validate, fieldDef.hint)

  console.assert(fieldDef, 'useFormFieldDef: fieldDef is null or undefined', fieldDef);

  const labelComponent = (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, lineHeight: 1 }}>
    <Box component="span">{fieldDef.displayName}</Box>
    {fieldDef.headerTooltip && (
      <HeaderTooltip content={<InfoTooltip fieldDef={fieldDef} formValues={formValues} />}>
        <IconButton size="small" sx={{ p: 0, width: 16, height: 16 }}>
          <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: 14, color: '#666' }} />
        </IconButton>
      </HeaderTooltip>
    )}
  </Box>
  );

  
  return {
    // Passed along from useFormField()
    onBlur,
    error,
    helperText,

    // Supplied by useFormFieldDef()
    value: formValues[fieldDef.field],
    setValue: (value) => assignValue(fieldDef.field, value),
    label: labelComponent,
    disabled: fieldDef.disabled,
  };
};